import React, { useContext, useEffect } from "react";
import {
    ButtonRenderProps,
    ButtonProps,
    ContentSection,
    GridRow,
    GridColumn,
    TableFilter,
    MultiselectSearchDropdown,
    ClearFiltersButton,
    ListTable,
    Grid,
    SearchBox,
    Stack,
    Checkbox,
    DetailsRowProps,
    Theme,
    mapToDropdownOption,
    RenderFunction,
    DropdownMultiselect
} from "sprint-common-web";

import { TagElementsList } from "../../../common/components/TagElementsList";
import { AirlinesDataContext } from "../../../common/data/lookup-data-providers/AirlinesDataProvider";
import { VersionTypesDataContext } from "../../../common/data/lookup-data-providers/VersionTypesDataProvider";
import { AncillaryContentSubtype } from "../../../common/types/AncillaryContentType";
import { ContentSubtype } from "../../../common/types/ContentSubtype";
import { HeaderBreadcrumb } from "../../../common/ui/layout/header/header-breadcrumb/HeaderBreadcrumb";
import { PageHeader } from "../../../common/ui/layout/header/PageHeader";
import { contentManageRoutes } from "../../ContentRoutes";
import { ManageAncillaryContext } from "../context/ManageAncillaryContext";
import { AncillaryItem } from "./AncillaryItem";
import { AddAncillaryButton } from "./buttons/AddAncillaryButton";
import { MergeAncillariesButton } from "./buttons/MergeAncillariesButton";
import { createAncillaryListingColumns } from "./createAncillaryListingColumns";
import { useAncillaryListing } from "./useAncillaryListing";
import { sourceOptions, useAncillaryListingFilters } from "./useAncillaryListingFilters";
import { canBeAddedToMergeList } from "./utils/canBeAddedToMergeList";

export const AncillaryListing: React.FC = () => {
    const { airlines } = useContext(AirlinesDataContext);
    const { nonTrailerVersionTypes: versionTypes } = useContext(VersionTypesDataContext);
    const { ancillaryContentType } = useContext(ManageAncillaryContext);
    const {
        filters,
        setSort,
        setPage,
        setPageSize,
        setTitle,
        clearFilters,
        setIncludeMerged,
        setBrandings,
        setVersionTypes,
        setSources
    } = useAncillaryListingFilters();
    const { totalItems, isListLoading, getData } = useAncillaryListing();

    const { ancillaryItems, ancillariesToMerge, removeAncillaryToMerge } = useContext(ManageAncillaryContext);

    const columns = createAncillaryListingColumns();

    useEffect(() => {
        getData(filters);
    }, [filters]);

    const handleIncludeMergedCheckboxChange = (
        _ev?: React.FormEvent<HTMLElement | HTMLInputElement> | undefined,
        checked?: boolean | undefined
    ) => {
        setIncludeMerged(checked ?? false);
    };

    const addNewAncillaryButtonProps: ButtonRenderProps<ButtonProps> = {
        render: () => AddAncillaryButton({ pageSize: filters.pageSize })
    };

    const mergeAncillariesButtonProps: ButtonRenderProps<ButtonProps> = {
        render: MergeAncillariesButton
    };

    const renderRow = (rowProps?: DetailsRowProps, renderDefault?: RenderFunction<DetailsRowProps>) => {
        if (canBeAddedToMergeList(ancillariesToMerge, rowProps?.item) && renderDefault) {
            return renderDefault(rowProps, renderDefault);
        }
        const isSelected = ancillariesToMerge
            .map((ancillary: AncillaryItem) => ancillary.id)
            .includes(rowProps?.item.id);

        return renderNotMergeableRow(isSelected, rowProps, renderDefault);
    };

    return (
        <>
            <PageHeader>
                <HeaderBreadcrumb routes={contentManageRoutes} />
            </PageHeader>
            <ContentSection
                header={getHeaderTitleResolver(ancillaryContentType)}
                buttons={[mergeAncillariesButtonProps, addNewAncillaryButtonProps]}
            >
                <Grid>
                    <GridRow>
                        <TagElementsList
                            items={ancillariesToMerge}
                            onItemDismiss={removeAncillaryToMerge}
                            renderItemText={(item: AncillaryItem) => item.title}
                            styles={{ root: { paddingBottom: 10 } }}
                        />
                    </GridRow>
                    <GridRow>
                        <GridColumn sm={3}>
                            <SearchBox placeholder="Title" onChange={setTitle} value={filters.title} />
                        </GridColumn>
                        <GridColumn sm={2}>
                            <TableFilter>
                                <DropdownMultiselect
                                    testId="ancillaries-filters-sources"
                                    placeholder="Source"
                                    selectedKeys={filters.sources}
                                    onChange={setSources}
                                    options={sourceOptions}
                                />
                            </TableFilter>
                        </GridColumn>
                        <GridColumn sm={2}>
                            <TableFilter>
                                <Checkbox
                                    label="Include Merge Sources"
                                    onChange={handleIncludeMergedCheckboxChange}
                                    checked={filters.includeMerged ?? false}
                                />
                            </TableFilter>
                        </GridColumn>
                        <GridColumn sm={2}>
                            <TableFilter>
                                <MultiselectSearchDropdown
                                    placeholder="Airlines"
                                    options={airlines?.map((airline) => mapToDropdownOption(airline))}
                                    selectedKeys={filters.brandings}
                                    onChange={setBrandings}
                                    disabled={!airlines?.length}
                                />
                            </TableFilter>
                        </GridColumn>
                        <GridColumn sm={2}>
                            <TableFilter>
                                <MultiselectSearchDropdown
                                    placeholder="Version Type"
                                    options={versionTypes?.map((versionType) => mapToDropdownOption(versionType))}
                                    selectedKeys={filters.versionTypes}
                                    onChange={setVersionTypes}
                                    disabled={!versionTypes?.length}
                                />
                            </TableFilter>
                        </GridColumn>

                        <GridColumn sm={1}>
                            <Stack horizontalAlign="end">
                                <ClearFiltersButton onClear={clearFilters} isLabeled={true} />
                            </Stack>
                        </GridColumn>
                    </GridRow>
                    <GridRow>
                        <GridColumn sm={12}>
                            <ListTable
                                columns={columns}
                                items={ancillaryItems}
                                pagination={{
                                    ...filters,
                                    totalItems,
                                    onPageChange: setPage,
                                    onPageSizeChange: setPageSize
                                }}
                                sort={{ by: filters.sortBy, order: filters.sortOrder }}
                                onSortChange={setSort}
                                isLoading={isListLoading}
                                onRenderRow={ancillariesToMerge.length ? renderRow : undefined}
                            />
                        </GridColumn>
                    </GridRow>
                </Grid>
            </ContentSection>
        </>
    );
};

const getHeaderTitleResolver = (subtype: AncillaryContentSubtype) => {
    const ancillaryTypeResolver: Record<AncillaryContentSubtype, string> = {
        [ContentSubtype.Promo]: "Promos",
        [ContentSubtype.Safety]: "Safeties",
        [ContentSubtype.Advert]: "Adverts",
        [ContentSubtype.IndentWarning]: "Indent Warnings",
        [ContentSubtype.ClientSpecific]: "Client Specific"
    };

    return ancillaryTypeResolver[subtype];
};

const renderNotMergeableRow = (
    isSelected: boolean,
    rowProps?: DetailsRowProps,
    render?: RenderFunction<DetailsRowProps>
): JSX.Element | null => {
    if (render && rowProps)
        return render({
            ...rowProps,
            styles: {
                root: {
                    backgroundColor: isSelected ? Theme.palette.neutralLighterAlt : undefined,
                    borderLeft: isSelected ? "3px solid #892fa3" : undefined
                }
            }
        });

    return null;
};
